<template>
  <div
    class="booking-recap"
    :class="{
      expanded: isExpanded,
      empty: (!center || !center.id) && (!service || !service.id),
    }"
  >
    <div class="booking-recap__header">
      <h2>{{ translationsStore.t("booking_recap_header") }}</h2>

      <button
        class="toggle-btn"
        @click="toggle"
      >
        {{
          isExpanded
            ? translationsStore.t("booking_recap_btn_less")
            : translationsStore.t("booking_recap_see_more")
        }}
      </button>
    </div>
    <div class="booking-content">
      <div
        v-if="currentStep === 1"
        class="desc"
      >
        <p v-if="journeyType === 'center'">
          {{ translationsStore.t("booking_recap_content") }}
        </p>
        <p v-if="journeyType === 'service'">
          {{ translationsStore.t("booking_recap_content_service") }}
        </p>
      </div>
      <div
        v-if="
          (bookingStore.currentCenter &&
            currentStep !== 1 &&
            journeyType === 'center') ||
          (bookingStore.currentCenter &&
            journeyType === 'service' &&
            !hasMultipleCenters)
        "
        class="information"
      >
        <strong>{{ bookingStore.currentCenter.title }}</strong>
        <address v-if="bookingStore.currentCenter.adresse_postale_booking">
          {{ bookingStore.currentCenter.adresse_postale_booking }}
        </address>
      </div>
      <div
        v-if="bookingStore?.service?.id"
        class="treatment-information"
      >
        <div class="image-container">
          <img
            :src="bookingStore.service.image?.url"
            :alt="
              bookingStore.service.visuel_spa_booking?.altText ||
              bookingStore.service.image?.altText
            "
          />
        </div>
        <div class="text-container">
          <strong>{{ bookingStore.service.title }}</strong>
          <p v-if="calculatedPrice && bookingStore.currentShopifyVariant">
            {{ `${calculatedPrice} ${currencySymbol}` }}
          </p>
          <p
            v-if="calculatedDuration"
            class="duration"
          >
            {{ calculatedDuration }}<span class="type">{{ participants }}</span>
          </p>
          <p
            v-if="bookingStore.bookingSlot?.slot"
            class="date"
          >
            {{
              formatDetailsDate(
                new Date(bookingStore.bookingSlot?.slot),
                locale
              )
            }}
          </p>
        </div>
      </div>
      <div
        v-if="
          (customerData && currentStep >= 3 && journeyType === 'center') ||
          (customerData && currentStep >= 2 && journeyType === 'service')
        "
        class="beneficiary-information"
      >
        <h2>{{ translationsStore.t("booking_recap_user_info_title") }}</h2>
        <p>
          {{ bookingStore.currentUser?.firstName }}
          {{ bookingStore.currentUser?.lastName }}
        </p>
        <p>{{ bookingStore.currentUser?.email }}</p>
      </div>
      <div
        v-if="!customerData && stepReservation"
        class="connection-block"
      >
        <strong>{{
          translationsStore.t("booking_recap_connection_block_title")
        }}</strong>
        <div class="buttons-wrapper">
          <AnimatedButton
            :label="translationsStore.t('connexion_cta_label')"
            class="login"
            :href="loginUrl"
          />
          <AnimatedButton
            :label="translationsStore.t('registration_cta_label')"
            class="register secondary"
            :href="registerUrl"
          />
        </div>
      </div>
      <AnimatedButton
        v-if="stepReservation"
        :label="translationsStore.t('booking_item_cta')"
        class="add-cart-btn"
        :disabled="!hasSlotSelected || !customerData"
        :loading="buttonLoading"
        @click="addToCart()"
      />
    </div>
  </div>
</template>
<script setup>
import {
  defineProps,
  ref,
  defineEmits,
  onMounted,
  nextTick,
  computed,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { useDialog } from "naive-ui";
import { formatDetailsDate } from "../../utils/formatDate";
import { useBookingStore } from "../../stores/booking";
import { useTranslationsStore } from "../../stores/translations";
import AnimatedButton from "../AnimatedButton.vue";
import { formatCurrency } from "../../utils/formatCurrency";
import { idCenter } from "../../utils/idCenter";

const translationsStore = useTranslationsStore();
const bookingStore = useBookingStore();
const dialog = useDialog();

const props = defineProps({
  center: {
    type: Object,
    required: true,
  },
  service: {
    type: Object,
    required: true,
  },
  currentStep: {
    type: String,
    required: true,
  },
  maxStepReached: {
    type: String,
    required: true,
  },
  beneficiary: {
    type: Object,
    required: true,
  },
  hasMultipleCenters: {
    type: Boolean,
    default: () => false,
  },
});

const emit = defineEmits(["selectItem", "itemAddedToCart"]);

const isExpanded = ref(false);
const route = useRoute();
const router = useRouter();
const loading = ref(true);
const error = ref(null);
const currentShopifyVariantId = ref(null);
const customerData = ref(null);
const reservationId = ref(null);
const bookingId = ref(null);
const invoiceId = ref(null);
const expiryTime = ref(null);
const hasSlotSelected = ref(false);
const buttonLoading = ref(false);
const lang = route.params.lang;
const { environment } = window.themeVariables;

const loginUrl = computed(() => {
  return `${lang ? "/" + lang : ""}/account/login?return_url=${encodeURIComponent(route.fullPath)}`;
});
const registerUrl = computed(() => {
  return `${lang ? "/" + lang : ""}/account/register?return_url=${encodeURIComponent(route.fullPath)}`;
});
const journeyType = computed(() => bookingStore.journeyType);
const stepReservation = computed(() => {
  const step = Number(props.currentStep);
  return (
    (journeyType.value === "center" && step === 3) ||
    (journeyType.value === "service" && step === 2)
  );
});
const calculatedPrice = computed(() => {
  const price = bookingStore.currentShopifyVariant?.node?.priceV2?.amount;
  const formattedPrice = parseFloat(price)?.toLocaleString("default", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formattedPrice ? formattedPrice : "--";
});
const calculatedDuration = computed(() => {
  return bookingStore.currentDuration || "--";
});
const currencySymbol = computed(() => {
  const currencyCode =
    bookingStore.currentService?.currencyCode ||
    bookingStore.currentShopifyVariant?.node?.priceV2?.currencyCode;
  return formatCurrency(currencyCode);
});

const participants = computed(() => {
  const options = bookingStore.currentParticipant;
  return options || "Solo";
});

const toggle = () => {
  isExpanded.value = !isExpanded.value;
};
const locale = window.shopLocale || "fr-FR";
let isAddingToCart = false;
const addToCart = async () => {
  if (isAddingToCart) {
    console.warn("⏳ addToCart already in progress, ignoring click...");
    return;
  }
  // ✅ Ensure variant is available
  if (!currentShopifyVariantId.value) {
    console.error("❌ No Shopify variant ID available, aborting addToCart.");
    alert("Une erreur est survenue : aucune variante n’a été trouvée.");
    return;
  }
  isAddingToCart = true;

  try {
    if (bookingStore.currentUser) {
      console.log("👤 User logged in, reserving slot...");
      buttonLoading.value = true;
      await nextTick();
      await reserveZenotiSlot();
    } else {
      console.log("🔑 User not logged in, redirecting to login...");
      const currentUrl = route.fullPath;
      const returnUrl = encodeURIComponent(currentUrl + "&nextStep=true");
      window.location.href =
        window.location.origin +
        (lang ? `/${lang}` : "") +
        "/account/login?return_url=" +
        returnUrl;
    }
  } catch (err) {
    console.error("❌ Error during addToCart:", err);
    alert("Une erreur est survenue. Veuillez réessayer.");
  } finally {
    isAddingToCart = false;
    buttonLoading.value = false;
  }
};
console.log("bookingStore.currentCenter = ", bookingStore.currentCenter);
const reserveZenotiSlot = async () => {
  const query =
    window.location.origin === "http://127.0.0.1:9292"
      ? `${environment.variables.bff_url}/pms/booking/reserve?guestId=6875de5c-7569-4d62-907a-e0435812bf9a`
      : `${environment.variables.bff_url}/pms/booking/reserve`;
  try {
    const response = await axios.post(
      query,
      {
        centerId: idCenter(bookingStore.currentCenter.id_center),
        serviceId: bookingStore.zenotiService?.id,
        slotTime: bookingStore.bookingSlot?.slot,
        therapistGender: "ANY",
      },
      {
        headers: {
          "shopify-edge-metadata-shop-id":
            environment.variables.shopify_shop_id,
          "ngrok-skip-browser-warning":
            environment.variables.ngrok_skip_warning,
        },
      }
    );

    const newReservations =
      bookingStore.reservations?.length > 0
        ? [...bookingStore.reservations, response.data]
        : [response.data];
    console.log("newReservations = ", newReservations);
    bookingStore.$patch({
      zenotiReservations: [...newReservations],
    });
    await nextTick();

    document.dispatchEvent(new Event("newReservation", { bubbles: true }));
    emit("selectItem");
    await addCartShopify();

    // wait until the service is actually added to cart before showing confirmation
    window.setTimeout(() => {
      emit("selectItem");
    }, 1000);
  } catch (err) {
    error.value = "Failed to fetch data";
    console.error("❌ Error in reserveZenotiSlot:", err);
    if (err.response.data.statusCode === 412) {
      dialog.error({
        title: translationsStore.t("booking_slot_error_title"),
        content: translationsStore.t("booking_slot_error_content"),
        positiveText: translationsStore.t("booking_slot_error_button"),
        showIcon: false,
        titleStyle: {
          fontFamily: "var(--heading-font-family),serif",
        },
        titleClass: "reservation-error-dialog",
        positiveButtonProps: {
          type: "primary", // Naive UI type (primary, error, warning, etc.)
          size: "large", // small | medium | large
          round: false, // rounded corners
          backgroundColor: "rgb(var(--brand-black) / 1)",
          color: "rgb(var(--brand-black) / 1)",
          textColor: "white", // text color
          strong: true, // bolder font weight
          style: {
            padding: "20px 32px",
            letterSpacing: "0.48px",
            width: "100%",
            fontSize: ".75rem",
            fontFamily: "var(--text-font-family),sans-serif",
            textTransform: "uppercase",
            textAlign: "center",
          },
          contentStyle: {
            margin: "1.4375rem 0",
            fontSize: "1rem",
            lineHeight: "normal",
          },
        },
        closable: false,
        onPositiveClick: () => {
          console.log("Refreshing page to update calendar...");
          router.go(0);
        },
        onMaskClick: () => {
          console.log("Refreshing page to update calendar...");
          router.go(0);
        },
      });
    }
  } finally {
    loading.value = false;
  }
};

const addCartShopify = async () => {
  try {
    // Ensure we have a variant
    currentShopifyVariantId.value =
      bookingStore.currentShopifyVariant?.node?.id;
    if (!currentShopifyVariantId.value) {
      console.error("❌ Variant ID not set yet, aborting cart add.");
      return;
    }

    await nextTick();

    const reservation = bookingStore.zenotiReservations.find(
      (reservation) =>
        reservation.center_code === bookingStore.bookingSlot?.centerId &&
        reservation.service_code === bookingStore.bookingSlot?.serviceId &&
        reservation.slot_time === bookingStore.bookingSlot?.slot
    );

    if (!reservation) {
      console.error(
        "❌ No matching reservation found for slot:",
        bookingStore.bookingSlot
      );
      alert(
        "Une erreur est survenue lors de la réservation. Veuillez réessayer."
      );
      return;
    }

    reservationId.value = reservation.reservation_id;
    bookingId.value = reservation.booking_id;
    invoiceId.value = reservation.invoice_id;
    expiryTime.value = reservation.expiry_time;
    console.log("reservation = ", reservation);
    const date = new Date(bookingStore.bookingSlot?.slot);
    const locale =
      translationsStore.getShopifyLocale() || navigator.language || "fr-FR";
    const formattedDate = formatDetailsDate(date, locale);
    const payload = {
      id: currentShopifyVariantId.value.split("/").pop(),
      quantity: 1,
      properties: {
        _booking: "true", // marker to identify in cart drawer
        _centerId: idCenter(bookingStore.currentCenter.id_center),
        _serviceId: bookingStore.zenotiService?.id,
        _date: bookingStore.bookingSlot?.slot,
        _bookingId: bookingId.value,
        _reservationId: reservationId.value,
        _reservationExpiryTime: expiryTime.value,
        _invoiceId: invoiceId.value,
        Date: formattedDate, // Formatted date to display in cart and in checkout recap
      },
    };
    console.log("payload = ", payload);

    // Ensure cart is ready, then add item with properties
    const resp = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Shopify cart add failed: ${text}`);
    }
    const data = await resp.json();
    console.log("✅ Item added to cart:", data);
    emit("itemAddedToCart");

    // Trigger a custom event so your theme’s JS refreshes the drawer
    document.dispatchEvent(new Event("cart:refresh"));
  } catch (err) {
    console.error("❌ Error adding item to cart:", err);
    alert(
      "Une erreur est survenue lors de l'ajout au panier. Veuillez réessayer."
    );
  }
};

watch(
  () => bookingStore.zenotiVariantService?.code,
  () => {
    currentShopifyVariantId.value =
      bookingStore.currentShopifyVariant?.node?.id;
  }
);

watch(
  () => bookingStore.currentShopifyVariant,
  () => {
    console.log(
      "bookingStore.currentShopifyVariant changed",
      bookingStore.currentShopifyVariant?.node?.id
    );
    currentShopifyVariantId.value =
      bookingStore.currentShopifyVariant?.node?.id;
  }
);

watch(
  () => bookingStore.currentBookingSlot?.slot,
  () => {
    if (!bookingStore.currentBookingSlot?.slot) {
      hasSlotSelected.value = false;
    } else {
      hasSlotSelected.value = true;
      currentShopifyVariantId.value =
        bookingStore.currentShopifyVariant?.node?.id;
    }
  }
);

onMounted(() => {
  const storedData = localStorage.getItem("customer-storage");
  if (storedData) {
    customerData.value = JSON.parse(storedData);
    bookingStore.updateUser(customerData.value);

    if (route.query.nextStep) {
      nextTick(() => {
        addToCart();
      });
    }
  } else {
    bookingStore.updateUser(null);
  }

  currentShopifyVariantId.value = bookingStore.currentShopifyVariant?.node?.id;

  if (bookingStore.currentBookingSlot?.slot) {
    hasSlotSelected.value = true;
  }
});
</script>
<style scoped>
/* Your styles */
.booking-content {
  display: none;
}
.booking-recap__header {
  display: flex;
  justify-content: space-between;
}
.booking-recap.expanded .booking-content {
  display: block;
  margin-top: 0.5rem;
}
.booking-recap.empty {
  position: static;
}
.booking-recap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  bottom: 0;
  margin-inline-start: -1.25rem;
  width: calc(100% + 2.5rem);
  z-index: 10;
}
.booking-app .booking-recap .toggle-btn {
  background: transparent;
  padding: 0;
  border: none;
  color: rgb(var(--brand-black) / 1);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.44px;
  text-transform: uppercase;
  text-decoration: underline;
}
.booking-recap .toggle-btn:hover,
.booking-recap .toggle-btn:focus,
.booking-recap .toggle-btn:active {
  text-decoration: none;
}
.booking-recap h2 {
  font-size: 1.125rem;
  font-weight: 600;
}
.booking-recap strong {
  display: block;
  margin-block-end: 0.75rem;
  font-family: "neue-haas-grotesk-display", sans-serif;
  font-size: 1rem;
  font-weight: 500;
}
.booking-recap address {
  opacity: 0.6;
  font-size: 1rem;
  font-style: normal;
}
.booking-recap .image-container {
  width: 5.5rem;
  height: 5.5rem;
}
.booking-recap .image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.booking-recap .treatment-information {
  display: flex;
  gap: 1.5rem;
  margin-block-start: 1rem;
}
.booking-recap .treatment-information .date::first-letter {
  text-transform: capitalize;
}
.booking-recap .treatment-information strong {
  font-family: var(--heading-font-family);
  font-size: 1.5rem;
  line-height: 1.35rem;
}
.booking-recap .treatment-information strong + p {
  margin: 0 0 1.5rem;
  font-size: 1rem;
}
.booking-recap .type {
  display: flex;
  align-items: center;
  font-size: 1rem;
}
.booking-recap .type::before {
  content: "";
  width: 1px;
  height: 1rem;
  background-color: rgba(0 0 0 / 30%);
  display: block;
  margin: 0 0.75rem 0 0.25rem;
}
.booking-recap .beneficiary-information {
  padding: 3rem 0 1.5rem;
  font-size: 1rem;
}
.booking-recap .beneficiary-information p {
  margin-top: 0.62rem;
}
.booking-recap .add-cart-btn {
  width: 100%;
  margin-top: 1rem;
}
.add-cart-btn:disabled {
  opacity: 0.3;
  cursor: default;
}
.booking-recap .connection-block {
  margin-block-start: 2rem;
  padding: 1.5rem;
  background-color: rgb(var(--brand-white) / 1);
}
.booking-recap .button.login,
.booking-recap .button.register {
  display: block;
  /* width: 100%; */
}
.booking-recap .button.register {
  margin-top: 0.5rem;
}
.booking-recap .connection-block strong {
  font: 1.125rem/1.3 var(--text-font-family);
  font-weight: 600;
}
.booking-recap:has(.information) .desc {
  margin-block: 1rem;
}
@media screen and (min-width: 1000px) {
  .booking-recap {
    margin-inline-start: 0;
    width: auto;
    position: static;
    z-index: 1;
  }
  .booking-content {
    display: block;
    gap: 2rem;
  }
  .toggle-btn {
    display: none;
  }
  .booking-recap .image-container {
    width: 9.125rem;
    height: 9.125rem;
  }
  .booking-recap .beneficiary-information {
    padding-top: 3.5rem;
  }
  .booking-recap .add-cart-btn {
    margin-top: 2rem;
  }
  .booking-recap .buttons-wrapper {
    display: flex;
    gap: 1rem;
  }
  .booking-recap .buttons-wrapper .button {
    width: 50%;
  }
  .booking-recap .button.register {
    margin-top: 0;
  }
  .booking-recap strong {
    margin-block-end: 1rem;
  }
  .booking-recap h2 {
    font-size: 1.25rem;
  }
}
</style>
npm notice npm notice New major version of npm available! 10.9.3 -> 11.6.2 npm
notice Changelog: https://github.com/npm/cli/releases/tag/v11.6.2 npm notice To
update run: npm install -g npm@11.6.2 npm notice
